import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  use,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PaginationProps } from '@fabric-ui/components/Table';
import { isDeepEqual } from '@fabric-ui/utils';
import { useStateWithHistory } from '@hooks/useStateWithHistory';
import { useLocation, useNavigate, useParams } from '@utils/AvRouter';
import { generateID } from '@utils/utils';
import { FileAnalyticsContext } from '@views/Sources/FileAnalyticsContext';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../../context/AvContextProvider';
import { PAGE_PATHS, Permission } from '../../../types';
import { useDataAndFieldsPreview, useDataStreamInstanceAndFieldsPreview, useDefaultMappings, useDSIData, useMapping } from '../hooks';
import { FileParser } from '../types';
import { useMappingFileFields } from './hooks/useMappingFileFields';
import { useMappingPreviewData } from './hooks/useMappingPreviewData';
import { useMappingValidation } from './MappingValidationContext';
import {
  DragItem,
  DragItemType,
  FieldMapping,
  FieldValueType,
  MapFilterType,
  MappingFieldType,
  MappingTreeNode,
  MappingType,
} from './types';
import { createField, getEntitiesShouldBeOpen, isMappingExploded } from './utils';

export const filterFieldsBySearch = (
  fields: MappingTreeNode[],
  searchTerm: string,
  isMappedFilterResult: (s: string) => boolean
): MappingTreeNode[] =>
  fields
    .map(field => {
      const filteredChildren = filterFieldsBySearch(field.children || [], searchTerm, isMappedFilterResult);
      return field.label.toLowerCase().includes(searchTerm) && isMappedFilterResult(field.label)
        ? field
        : filteredChildren.length
          ? { ...field, children: filteredChildren }
          : null;
    })
    .filter(Boolean) as MappingTreeNode[];

export interface MappingContextProps {
  dsiName: string;
  mappings: FieldMapping[];
  mappingsHistory: FieldMapping[][];
  mappingsCurrentStep: number;
  setMappings: Dispatch<SetStateAction<FieldMapping[]>>;
  isDirty: boolean;
  isReadOnly: boolean;
  fileParser: { fileParser: FileParser };
  setFileParser: Dispatch<SetStateAction<{ fileParser: FileParser }>>;
  mappingSearch: string;
  setMappingSearch: Dispatch<SetStateAction<string>>;
  parsedFieldsSearch: string;
  setParsedFieldsSearch: Dispatch<SetStateAction<string>>;
  parsedFieldsMappedFilter: MapFilterType | undefined;
  setParsedFieldsMappedFilter: Dispatch<SetStateAction<MapFilterType | undefined>>;
  showEditor: boolean;
  setShowEditor: Dispatch<SetStateAction<boolean>>;
  showPreview: boolean;
  setShowPreview: Dispatch<SetStateAction<boolean>>;
  previewPopperCol: string;
  setPreviewPopperCol: Dispatch<SetStateAction<string>>;
  editorValue: string | string[];
  setEditorValue: Dispatch<SetStateAction<string | string[]>>;
  editorType: MappingType;
  setEditorType: Dispatch<SetStateAction<MappingType>>;
  entityName: string | undefined;
  setEntityName: Dispatch<SetStateAction<string | undefined>>;
  editEntityField: string | undefined;
  setEditEntityField: Dispatch<SetStateAction<string | undefined>>;
  editDefaultEntityField: string | undefined;
  setDefaultEntityField: Dispatch<SetStateAction<string | undefined>>;
  editEntityFieldType: MappingFieldType | undefined;
  setEditEntityFieldType: Dispatch<SetStateAction<MappingFieldType | undefined>>;
  editorIsExploded: boolean | undefined;
  setEditorIsExploded: Dispatch<SetStateAction<boolean | undefined>>;
  fieldsList: MappingTreeNode[];
  explodedField: string | undefined;
  setExplodedField: Dispatch<SetStateAction<string | undefined>>;
  expandedMappedEntities: Record<string, boolean>;
  setExpandedMappedEntities: Dispatch<SetStateAction<Record<string, boolean>>>;
  expandedEntities: Record<string, boolean>;
  setExpandedEntities: Dispatch<SetStateAction<Record<string, boolean>>>;
  originalObject: any;
  entitiesMappedFilter: MapFilterType | undefined;
  setEntitiesMappedFilter: Dispatch<SetStateAction<MapFilterType | undefined>>;
  entitiesSearch: string;
  setEntitiesSearch: Dispatch<SetStateAction<string>>;
  dsiData: any;
  isDsiLoading: boolean;
  mappedEntityFields: MutableRefObject<Record<string, HTMLElement | null>>;
  mappedParsedFields: MutableRefObject<Record<string, HTMLElement | null>>;
  entitiesRefsMap: MutableRefObject<Record<string, HTMLElement | null>>;
  parsedFieldsRefsMap: MutableRefObject<Record<string, HTMLElement | null>>;
  originalMappings: FieldMapping[];
  getFieldType: (field: string | undefined, entityName: string | undefined) => any;
  isInSearch: (row: FieldMapping) => boolean;
  visibleMappings: FieldMapping[];
  previewData: any;
  dataFields: any;
  isLoadingFieldsPreview: boolean;
  hasFieldsDataLoaded: boolean;
  forceRender: () => void;
  visibleParsedFields: MappingTreeNode[];
  parsedFields: any;
  reset: () => void;
  dragItem: DragItemType | null;
  setDragItem: Dispatch<SetStateAction<DragItemType | null>>;
  handleDrop: (type: DragItem, dragItem: DragItemType) => void;
  entityDropRef: MutableRefObject<HTMLElement | undefined>;
  parsedFieldDropRef: MutableRefObject<HTMLElement | undefined>;
  undo: () => void;
  canUndo: boolean;
  redo: () => void;
  canRedo: boolean;
  clearMappings: (mappingState?: SetStateAction<FieldMapping[]>) => void;
  previewPaginationProps?: PaginationProps;
  revertToDefaultMappings: () => void;
  workflowId: string | undefined;
  isLoadingWorkflowId: boolean;
  isDefaultMappings: boolean;
  cleanState: () => void;
  isLoadingPreviewMappingQuery: boolean;
  isRefetchingPreviewMappingQuery: boolean;
  isPresentationMode: boolean;
  isLoadingMappings: boolean;
  previewRows: any[] | undefined;
  hasExplodedField: boolean;
  isStaleWorkflow: boolean;
}

export const MappingContext = React.createContext<MappingContextProps>({} as MappingContextProps);

export const MappingContextProvider = ({ children, dataSourceName }: { children: React.ReactNode; dataSourceName?: string }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id: instanceId, type, previewId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: isDsiLoading, data: dsiData } = useDSIData({ instanceId });
  const dsiName = dsiData?.name ?? dataSourceName;
  const { clearValidationMessages } = useMappingValidation();

  const onHistoryChange = (newMappings: FieldMapping[] | undefined) => {
    const newExplodedField = newMappings?.find(isMappingExploded);
    const explodedMappingValue = (
      newExplodedField?.fieldValueType === FieldValueType.MappingValue
        ? newExplodedField
        : newExplodedField?.compositeValue?.fields?.find(f => f.exploded)
    )?.mappingValue;

    if (explodedMappingValue && explodedField !== explodedMappingValue) {
      setExplodedField(explodedMappingValue);
    }

    clearValidationMessages();
    reset();
  };

  const {
    currentState: mappings,
    setState: setMappingsDirect,
    clearHistory: clearMappings,
    undo,
    canUndo,
    redo,
    canRedo,
    isInitialState,
    history: mappingsHistory,
    currentStep: mappingsCurrentStep,
  } = useStateWithHistory<FieldMapping[]>(state?.mappingsHistoryState || state?.mapping || [], {
    allowKeyboardShortcuts: true,
    onUndo: onHistoryChange,
    onRedo: onHistoryChange,
  });

  const setMappings = (arg: React.SetStateAction<FieldMapping[]>) => {
    cleanState();
    setMappingsDirect(arg);
  };

  const isDirty = useMemo(() => !isInitialState, [isInitialState]);

  const entitiesShouldBeOpen = state?.mapping || state?.mappingsHistoryState ? getEntitiesShouldBeOpen(mappings) : { General: true };
  const [fileParser, setFileParser] = useState<{ fileParser: FileParser }>(state?.fileParser);
  const [mappingSearch, setMappingSearch] = useState('');
  const [parsedFieldsSearch, setParsedFieldsSearch] = useState('');
  const [parsedFieldsMappedFilter, setParsedFieldsMappedFilter] = useState<MapFilterType>();
  const [editorValue, setEditorValue] = useState<string | string[]>('');
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorType, setEditorType] = useState<MappingType>(MappingType.Script);
  const [entityName, setEntityName] = useState<string>();
  const [editEntityField, setEditEntityField] = useState<string>();
  const [editDefaultEntityField, setDefaultEntityField] = useState<string>();
  const [editEntityFieldType, setEditEntityFieldType] = useState<MappingFieldType>();
  const [previewPopperCol, setPreviewPopperCol] = useState<string>('');
  const [expandedEntities, setExpandedEntities] = useState(entitiesShouldBeOpen);
  const [originalObject, setOriginalObject] = useState<any>({});
  const [entitiesMappedFilter, setEntitiesMappedFilter] = useState<MapFilterType>();
  const [entitiesSearch, setEntitiesSearch] = useState('');

  const [editorIsExploded, setEditorIsExploded] = useState<boolean>();
  const [expandedMappedEntities, setExpandedMappedEntities] = useState<Record<string, boolean>>(entitiesShouldBeOpen);

  const mappedEntityFields = useRef<Record<string, HTMLElement | null>>({});
  const mappedParsedFields = useRef<Record<string, HTMLElement | null>>({});
  const entitiesRefsMap = useRef<Record<string, HTMLElement | null>>({});
  const parsedFieldsRefsMap = useRef<Record<string, HTMLElement | null>>({});

  const {
    userPermissions: { hasOnlyPermission },
    accountEntities: { oldIngressProjs: entityFields, fieldTypeMap },
  } = useAvContext();

  const entityDropRef = useRef<HTMLElement>(undefined);
  const parsedFieldDropRef = useRef<HTMLElement>(undefined);
  const [dragItem, setDragItem] = useState<DragItemType | null>(null);
  const isReadOnly = hasOnlyPermission({ path: PAGE_PATHS.SOURCES_MAPPING, permission: Permission.READ });

  const cleanState = useCallback(() => {
    const { mapping, fileParser, ...newState } = state || {};
    navigate({ state: newState, ignoreBlocker: true });
  }, [state, navigate]);

  const { isLoadingWorkflowId, workflowId, isStaleWorkflow } = use(FileAnalyticsContext);

  const onSuccess = data => {
    setOriginalObject(data);
    if (!state?.mapping && !state?.mappingsHistoryState) {
      clearMappings(data.originalMappings);
      const entitiesShouldBeOpen = getEntitiesShouldBeOpen(data.originalMappings);
      setExpandedMappedEntities(entitiesShouldBeOpen);
      setExpandedEntities(entitiesShouldBeOpen);
      updateExplodedField(data.originalMappings);
    }
  };

  const updateExplodedField = (newMappings: FieldMapping[]) => {
    setExplodedField(
      newMappings
        .reduce(
          (explodedField, mapping) =>
            explodedField ??
            (mapping.mappingType === MappingType.Path && mapping.exploded
              ? mapping.mappingValue
              : mapping.mappingType === MappingType.Composite
                ? mapping.compositeValue?.fields.find(field => field.exploded)?.mappingValue
                : undefined),
          undefined as string | undefined
        )
        ?.split('.')?.[0]
    );
  };

  const isInSearch = useCallback(
    (fieldMapping: FieldMapping) => {
      const { entityName, modelFieldName, fieldValueType, mappingValue, compositeValue } = fieldMapping;
      if (!mappingSearch) {
        return true;
      }

      return [
        entityName,
        modelFieldName,
        fieldValueType === FieldValueType.MappingValue
          ? mappingValue
          : compositeValue.fields?.map(({ mappingValue }) => mappingValue).join(' '),
        entityFields[entityName].nameMap[modelFieldName],
      ]
        .map(v => v?.toLowerCase().trim())
        .some(v => v?.includes(mappingSearch.toLowerCase().trim()));
    },
    [mappingSearch]
  );

  const visibleMappings = useMemo(
    () =>
      mappings.filter(
        mapRow =>
          // filter those who are collapsed / in the scroll area or not in search.
          isInSearch(mapRow) && expandedEntities[mapRow.entityName] && expandedMappedEntities[mapRow.entityName]
      ),
    [mappingSearch, mappings, expandedEntities, expandedMappedEntities]
  );

  const { data: { originalMappings } = {}, isLoading: isLoadingDsiMappings } = useMapping(type, previewId, instanceId, onSuccess);

  const { data: defaultMappings, isLoading: isLoadingDefaultMappings } = useDefaultMappings(type);

  const getFieldType = useCallback(
    (field: string | undefined, entityName: string | undefined) =>
      fieldTypeMap[`${(entityName && entityFields[entityName]?.projId?.name) ?? entityName?.toLowerCase()}.${field ?? ''}`],
    [fieldTypeMap, entityFields]
  );

  const { data: previewDataFromWizard, isLoading: isLoadingPreviewFieldsFromWizard } = useDataAndFieldsPreview({
    previewId,
    type,
    fileParser: fileParser?.fileParser,
  });

  useEffect(() => {
    if (state?.fileParser || dsiData) {
      setFileParser(state?.fileParser || dsiData);
    }
  }, [state?.fileParser, dsiData]);

  const { data: previewDataFromDS, isLoading: isLoadingPreviewFromDs } = useDataStreamInstanceAndFieldsPreview(
    { instanceId, fileParser: fileParser?.fileParser },
    fileParser && dsiData
  );

  const [page, setPage] = useState(0);

  const {
    data: queryMappingPreviewData,
    totalRowCount: totalRowsCount,
    isLoading: isLoadingPreviewMappingQuery,
    isRefetching: isRefetchingPreviewMappingQuery,
  } = useMappingPreviewData({
    page,
    workflowId,
    enabled: !isLoadingWorkflowId,
    instanceId,
  });

  const {
    fields,
    keys,
    hasLoaded: hasFieldsDataLoaded,
    isLoading: isLoadingFieldsData,
  } = useMappingFileFields({
    instanceId,
    workflowId,
    enabled: !isLoadingWorkflowId && !isStaleWorkflow,
  });

  const dataFields = useMemo<MappingTreeNode[]>(
    () => (isStaleWorkflow ? (previewDataFromDS?.fields ?? previewDataFromWizard?.fields) : fields) ?? [],
    [isStaleWorkflow, previewDataFromDS?.fields, previewDataFromWizard?.fields, fields]
  );

  const previewData = queryMappingPreviewData || previewDataFromDS?.data || previewDataFromWizard?.data;

  const parsedFields = useMemo<string[]>(
    () =>
      isStaleWorkflow || keys.length === 0
        ? previewData?.[0]
          ? Object.keys(queryMappingPreviewData?.[0] ?? JSON.parse(previewData?.[0] || '{}'))
          : []
        : keys,
    [isStaleWorkflow, previewData, queryMappingPreviewData, keys]
  );

  const previewRows = useMemo(() => {
    if (previewData) {
      try {
        return previewData.map((row, index) => ({ ...(typeof row === 'string' ? JSON.parse(row) : row), avalor_unique_key: index }));
      } catch (e) {
        console.error({ e, previewData });
        enqueueSnackbar('Unable to parse fields, try to change parsing', { variant: 'error' });
        return [];
      }
    }
    return undefined;
  }, [previewData, enqueueSnackbar]);

  const isLoadingFieldsPreview = isLoadingPreviewFromDs || isLoadingPreviewFieldsFromWizard || isLoadingFieldsData;
  const [, updateState] = useState<number>();
  const forceRender = () => {
    requestAnimationFrame(() => updateState(Date.now()));
  };

  const reset = useCallback(() => {
    setDragItem(null);
    setEditEntityField(undefined);
    setDefaultEntityField(undefined);
    setEditorValue('');
    setEditorIsExploded(false);
    setEntityName(undefined);
    setShowEditor(false);
    setEditorType(MappingType.Script);
  }, []);

  const isDefaultMappings = useMemo(() => isDeepEqual(defaultMappings, mappings), [defaultMappings, mappings]);

  const revertToDefaultMappings = useCallback(() => {
    if (!isDefaultMappings && defaultMappings) {
      setMappings(defaultMappings);
      setExpandedMappedEntities(getEntitiesShouldBeOpen(defaultMappings));
      reset();
    }
  }, [setMappings, isDefaultMappings, defaultMappings, reset]);

  const parsedPreviewData = useMemo(
    () =>
      queryMappingPreviewData && previewData === queryMappingPreviewData
        ? previewData?.[0]
        : previewData?.[0]
          ? JSON.parse(previewData[0])
          : [],
    [previewData, queryMappingPreviewData]
  );

  const [explodedField, setExplodedField] = useState<string | undefined>(undefined);

  useEffect(() => {
    updateExplodedField(mappings);
  }, [dataFields]);

  const fieldsList = useMemo(() => {
    const isMappingItemPathMissing = (field: string): boolean =>
      !parsedFields.includes(field) &&
      parsedPreviewData?.[field] === undefined &&
      !!field &&
      !Array.isArray(field) &&
      field
        .split('.')
        .reduce(
          (current, key) => (current && typeof current === 'object' && key in current ? current[key] : undefined),
          parsedPreviewData
        ) === undefined;

    const missingMappedSet = mappings.reduce((set, { fieldValueType, mappingType, mappingValue }) => {
      if (mappingType === MappingType.Path && fieldValueType === FieldValueType.MappingValue && isMappingItemPathMissing(mappingValue)) {
        set.add(mappingValue);
      }
      return set;
    }, new Set<string>());

    const parseExplodedChildren = field =>
      field.isExploded ? [] : field.children.map(child => ({ ...child, label: `${child.path} exploded`, isExploded: true }));

    const parsedDataFields = dataFields.reduce(
      (list, field) => [
        ...list,
        ...(field.path === explodedField
          ? [
              { ...field, children: [], isExplodedParent: true },
              { ...field, label: `${field.path} exploded`, isExploded: true, children: parseExplodedChildren(field) },
            ]
          : [field]),
      ],
      [] as MappingTreeNode[]
    );

    return [...parsedDataFields, ...[...missingMappedSet].map(field => createField(`${field}`, true))];
  }, [dataFields, explodedField, mappings, parsedPreviewData]);

  const visibleParsedFields = useMemo(() => {
    const isMappedFilterResult = v => {
      const mappedParsedFields = mappings.map(mapRow => mapRow.mappingValue);
      return (
        !parsedFieldsMappedFilter ||
        (parsedFieldsMappedFilter === MapFilterType.Mapped ? mappedParsedFields.includes(v) : !mappedParsedFields.includes(v))
      );
    };

    return filterFieldsBySearch(fieldsList, parsedFieldsSearch.toLowerCase(), isMappedFilterResult);
  }, [fieldsList, mappings, parsedFieldsMappedFilter, parsedFieldsSearch]);

  const handleDrop = useCallback(
    (type: DragItem, dragItem) => {
      if (type === dragItem.type) {
        const label = dragItem.field.path || dragItem.field.label || dragItem.field;
        if (type === DragItem.Parsed) {
          setEditorValue(label);
          setEditorType(MappingType.Path);
          setShowEditor(false);
          setEditorIsExploded(dragItem.field.isExploded);
        }
        if (type === DragItem.Entity) {
          if (mappings.some(row => row.modelFieldName === label && row.entityName === dragItem.entityName)) {
            enqueueSnackbar('This field is already mapped', { variant: 'warning' });
            return;
          }
          setEditEntityField(label);
          setDefaultEntityField(generateID());
          setEntityName(dragItem.entityName);
        }
      }
      setDragItem(null);
    },
    [mappings]
  );

  const hasExplodedField = useMemo(
    () => mappings.some(({ exploded, compositeValue }) => exploded || compositeValue?.fields?.some(field => field.exploded)),
    [mappings]
  );

  const value = useMemo(
    () => ({
      dsiName,
      mappings,
      mappingsHistory,
      mappingsCurrentStep,
      setMappings,
      isDirty,
      isReadOnly,
      fileParser,
      setFileParser,
      mappingSearch,
      setMappingSearch,
      dsiData,
      isDsiLoading,
      parsedFieldsSearch,
      setParsedFieldsSearch,
      parsedFieldsMappedFilter,
      setParsedFieldsMappedFilter,
      showEditor,
      setShowEditor,
      showPreview,
      setShowPreview,
      previewPopperCol,
      setPreviewPopperCol,
      mappedEntityFields,
      mappedParsedFields,
      editorValue,
      setEditorValue,
      editorType,
      setEditorType,
      entityName,
      setEntityName,
      editEntityField,
      setEditEntityField,
      editDefaultEntityField,
      setDefaultEntityField,
      editEntityFieldType,
      setEditEntityFieldType,
      editorIsExploded,
      setEditorIsExploded,
      expandedMappedEntities,
      setExpandedMappedEntities,
      expandedEntities,
      setExpandedEntities,
      originalObject,
      setOriginalObject,
      originalMappings,
      getFieldType,
      isInSearch,
      visibleMappings,
      entitiesMappedFilter,
      setEntitiesMappedFilter,
      entitiesSearch,
      setEntitiesSearch,
      entitiesRefsMap,
      parsedFieldsRefsMap,
      previewData,
      dataFields,
      isLoadingFieldsPreview,
      hasFieldsDataLoaded,
      forceRender,
      visibleParsedFields,
      parsedFields,
      fieldsList,
      explodedField,
      setExplodedField,
      reset,
      dragItem,
      setDragItem,
      handleDrop,
      entityDropRef,
      parsedFieldDropRef,
      undo,
      canUndo,
      redo,
      canRedo,
      clearMappings,
      revertToDefaultMappings,
      isDefaultMappings,
      isLoadingPreviewMappingQuery,
      isRefetchingPreviewMappingQuery,
      cleanState,
      previewPaginationProps: { totalRows: totalRowsCount, pageNum: page, onPageChange: setPage },
      workflowId,
      isLoadingWorkflowId,
      isPresentationMode: false,
      isLoadingMappings: isLoadingDsiMappings || isLoadingDefaultMappings,
      previewRows,
      hasExplodedField,
      isStaleWorkflow,
    }),
    [
      dsiName,
      mappings,
      mappingsHistory,
      mappingsCurrentStep,
      setMappings,
      isDirty,
      isReadOnly,
      fileParser,
      mappingSearch,
      dsiData,
      isDsiLoading,
      parsedFieldsSearch,
      parsedFieldsMappedFilter,
      editorValue,
      showEditor,
      showPreview,
      editorType,
      previewPopperCol,
      entityName,
      editEntityField,
      editDefaultEntityField,
      editEntityFieldType,
      editorIsExploded,
      expandedMappedEntities,
      expandedEntities,
      originalObject,
      originalMappings,
      isInSearch,
      visibleMappings,
      entitiesMappedFilter,
      entitiesSearch,
      previewData,
      dataFields,
      isLoadingFieldsPreview,
      hasFieldsDataLoaded,
      getFieldType,
      visibleParsedFields,
      parsedFields,
      fieldsList,
      explodedField,
      reset,
      dragItem,
      handleDrop,
      undo,
      canUndo,
      redo,
      canRedo,
      clearMappings,
      revertToDefaultMappings,
      isDefaultMappings,
      isLoadingPreviewMappingQuery,
      isRefetchingPreviewMappingQuery,
      cleanState,
      totalRowsCount,
      page,
      workflowId,
      isLoadingWorkflowId,
      isLoadingDsiMappings,
      isLoadingDefaultMappings,
      previewRows,
      hasExplodedField,
      isStaleWorkflow,
    ]
  );

  return <MappingContext value={value}>{children}</MappingContext>;
};

export const useMappingContext = () => useContext(MappingContext);
